import React, { useEffect, useState } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { moeda, virgulaPonto, pontoVirgula } from '../../util/util';
import { FaCheck, FaDownload, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { FinField, FinInput, FinTextarea } from '../../components/FinField';
import { calcularRemessaExteriorEstimativa, resumirRemessaExteriorEstimativa, pagamentosExteriorEtapa } from './remessaExteriorEstimativaPolicy';
import FechamentoExteriorResumo from './FechamentoExteriorResumo';
import '../../css/financeiro-parcela-pagar.css';
import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { cambioBrlLinha, cambioGlobalOrcamento, fatorMoedaOrcamento, moedaDaLinhaFornecedor, simboloMoeda, temCambioProprio } from './moedaFornecedorPolicy';
import { calcularImpostoNfAccessPorEtapa, calcularTotaisBrlOrcamento } from './colunaVigentePolicy';
import { linhaFeeSoEmReal, moedaOrcamentoDeBrl, valorBrlLinha } from './valorBrlPolicy';
import { prepararExibicaoBrl } from './feeBaseInternacionalPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';

export default function ResumoOrcamento() {
    const { id } = useParams();
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');

    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [showModalAprovar, setShowModalAprovar] = useState(false);
    const [showModalReprovar, setShowModalReprovar] = useState(false);
    const [comentarios, setComentarios] = useState('');

    const calcularTotalAntecipado = (orcamentoitem) => {
        if (!orcamentoitem) return 0;
        let totalAntecipado = 0;
        orcamentoitem.forEach((item) => {
            if (item.subitem) {
                item.subitem.forEach((subitem) => {
                    if (subitem.tipopagamento === 'antecipado') {
                        totalAntecipado += virgulaPonto(subitem.totalFechamento || 0);
                    }
                });
            }
        });
        return totalAntecipado;
    };

    // Moeda do fornecedor (27/09 e 02/10): linha com moeda própria (inclusive Real em orçamento
    // estrangeiro) entra na moeda do orçamento pelo fator câmbio-dela ÷ câmbio global; legado × 1.
    const MOMENTO_CAMPO = { totalPreEvento: 'PreEvento', totalNegociado: 'Negociado', totalFechamento: 'Fechamento' };
    const calcularTotalModo = (orcamentoitem, campo, cambioGlobal = 1) => {
        if (!orcamentoitem) return 0;
        let total = 0;
        orcamentoitem.forEach((item) => {
            if (item.subitem) {
                item.subitem.forEach((subitem) => {
                    // Opção (D-0113): aparece na linha, não soma no total.
                    if (ehSomenteVisualizacao(subitem)) return;
                    const valor = virgulaPonto(subitem[campo] || 0);
                    const fator = temCambioProprio(subitem) ? fatorMoedaOrcamento(subitem, MOMENTO_CAMPO[campo], cambioGlobal) : 1;
                    total += isNaN(valor) ? 0 : valor * fator;
                });
            }
        });
        return total;
    };

    const buscarDados = () => {
        if (!id) return;
        setCarregando(true);
        api({ method: 'get', action: '/orcamento/' + id, token })
            .then((response) => {
                setDados(response.data.data || {});
            })
            .finally(() => setCarregando(false));
    };

    useEffect(() => {
        buscarDados();
        // eslint-disable-next-line
    }, [id]);

    const aprovar = () => {
        if (!comentarios.trim()) {
            toast.error('É necessário informar comentários para a aprovação');
            return;
        }
        api({
            method: 'post',
            action: '/orcamento/aprovar',
            token,
            data: { orcamentoIds: [id], comentarios }
        })
            .then(() => {
                toast.success('Orçamento aprovado com sucesso!');
                setShowModalAprovar(false);
                setComentarios('');
                buscarDados();
            })
            .catch((err) => {
                toast.error('Erro ao aprovar: ' + (err.response?.data?.error ?? 'Erro desconhecido'));
            });
    };

    const reprovar = () => {
        if (!comentarios.trim()) {
            toast.error('É necessário informar comentários para a reprovação');
            return;
        }
        api({
            method: 'post',
            action: '/orcamento/reprovar',
            token,
            data: { orcamentoIds: [id], comentarios }
        })
            .then(() => {
                toast.success('Orçamento reprovado com sucesso!');
                setShowModalReprovar(false);
                setComentarios('');
                buscarDados();
            })
            .catch((err) => {
                toast.error('Erro ao reprovar: ' + (err.response?.data?.error ?? 'Erro desconhecido'));
            });
    };

    const abrirNotaFiscal = (arquivoId) => {
        if (!arquivoId) return;
        window.open(`${process.env.REACT_APP_API_URL}/arquivo/download/${arquivoId}`, '_blank');
    };

    if (carregando) {
        return (
            <ErpTemplate>
                <div className="main-content container-fluid fin-pp">
                    <div className="fin-pp__card" style={{ padding: 24, textAlign: 'center' }}>
                        <p className="fin-pp__cell-muted" style={{ margin: 0 }}>Carregando orçamento...</p>
                    </div>
                </div>
            </ErpTemplate>
        );
    }

    if (!dados) {
        return (
            <ErpTemplate>
                <div className="main-content container-fluid fin-pp">
                    <div className="fin-pp__card" style={{ padding: 24 }}>
                        <p className="fin-pp__cell-muted" style={{ margin: 0 }}>Orçamento não encontrado.</p>
                    </div>
                </div>
            </ErpTemplate>
        );
    }

    const totalAntecipado = calcularTotalAntecipado(dados.orcamentoitem || []);
    const saldoDisponivel = virgulaPonto(dados.valorAdiantamento || 0) - totalAntecipado;

    const simbolos = { real: 'R$', dolar: 'US$', euro: '€', iene: '¥', libra: '£', dolarcad: 'C$' };
    const moedaAtual = ((dados.moeda || 'real')).toLowerCase();
    const cambio = parseFloat(dados.cambio || 1) || 1;
    const isInternacional = moedaAtual !== 'real' && cambio !== 1;
    const simbolo = simbolos[moedaAtual] || 'R$';

    const cambioGlobal = cambioGlobalOrcamento(dados);
    // Subtotal = linhas + Imposto NF Access automático dos grupos (Chiesi/internacional; não é linha),
    // pela mesma fórmula do editor e do valorTotal da API (colunaVigentePolicy).
    const impostoNfAccess = calcularImpostoNfAccessPorEtapa(dados);
    // Valor exato em reais (contrato 02/10): R$ = soma dos R$ das linhas + NF Access em R$. No modo R$
    // exato (orçamento estrangeiro com linha de moeda própria) o subtotal na moeda = R$ ÷ câmbio.
    // Item 3 (pós-QA 02/10): orçamento estrangeiro, inclusive legado, mostra o R$ pela conta exata
    // (FEE R$ = % × base em R$) numa cópia de exibição; nada do gravado muda.
    const exibicaoBrl = prepararExibicaoBrl(dados);
    // Round 3 (C/A): os subtotais incluem as taxas do exterior (como o Subtotal da tela e o painel) e o
    // Fechamento conciliado usa os valores de exibição do fechamento real (fechamento_exibicao da API:
    // pago em R$, FEE recalculado, encargos reais). Na moeda do orçamento = R$ ÷ câmbio, uma vez. Só
    // exibição: nada gravado muda.
    const totaisBrl = calcularTotaisBrlOrcamento(exibicaoBrl.orcamento, null, { fechamentoExibicao: true });
    const subtotalBrl = totaisBrl.totalComEncargos;
    const subtotalMoeda = (campo, chave) => (totaisBrl.estrangeiro
        ? moedaOrcamentoDeBrl(subtotalBrl[chave], cambioGlobal)
        : calcularTotalModo(dados.orcamentoitem || [], campo, cambioGlobal) + impostoNfAccess[chave]);
    const totalPreEvento = subtotalMoeda('totalPreEvento', 'preEvento');
    const totalNegociado = subtotalMoeda('totalNegociado', 'negociado');
    const totalFechamento = subtotalMoeda('totalFechamento', 'fechamento');
    // Célula Fechamento com exibição do fechamento exterior (pago/recalculado): valor exibido e o R$ dele.
    const textoFechamentoExibicao = (subitem) => {
        const exibicao = subitem.fechamento_exibicao;
        if (!exibicao) return null;
        const brl = exibicao.origem === 'ledger' ? virgulaPonto(exibicao.principal_brl) : virgulaPonto(exibicao.total_brl);
        if (linhaFeeSoEmReal(subitem, isInternacional)) return `R$ ${moeda(brl || 0)}`;
        const simboloLinha = temCambioProprio(subitem) ? simboloMoeda(moedaDaLinhaFornecedor(subitem, moedaAtual, 'Fechamento')) : simbolo;
        return `${simboloLinha} ${moeda(virgulaPonto(exibicao.totalFechamento) || 0)} · R$ ${moeda(brl || 0)}`;
    };
    // R$ da linha legada (regra 2); a linha de FEE no modo R$ exato = % × base R$ (refeito do gravado).
    const brlLinhaLegada = (subitem, campo) => {
        const exibicao = exibicaoBrl.porLinha.get(subitem);
        return valorBrlLinha(exibicao ? { ...subitem, valorBrlExibicao: exibicao } : subitem, MOMENTO_CAMPO[campo], cambioGlobal, { exato: totaisBrl.estrangeiro });
    };
    // Linha com moeda própria: valor na moeda DELA na etapa (08/10: moeda por etapa) + R$ pelo câmbio dela
    // na etapa; legado: como antes.
    const textoValorLinhaPropria = (subitem, campo) => {
        const valor = virgulaPonto(subitem[campo] || 0);
        const moedaLinha = moedaDaLinhaFornecedor(subitem, moedaAtual, MOMENTO_CAMPO[campo]);
        if (moedaLinha === 'real') return `R$ ${moeda(valor)}`;
        return `${simboloMoeda(moedaLinha)} ${moeda(valor)} · R$ ${moeda(valor * cambioBrlLinha(subitem, MOMENTO_CAMPO[campo], cambioGlobal))}`;
    };
    const remessaResumo = resumirRemessaExteriorEstimativa(dados);
    const podeAprovar = ['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro', 'financeiro', 'financeiro.credito', 'financeiro.adiantamento'].includes(perfil);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Resumo do Orçamento</h1>
                        <p className="fin-pp__subtitle">
                            {dados.nomeEvento ? `${dados.nomeEvento} · ${dados.titulo || 'Sem título'}` : (dados.titulo || 'Sem título')}
                        </p>
                    </div>
                    {podeAprovar ? (
                        <div className="fin-pp__actions">
                            <button type="button" className="fin-pp__btn fin-pp__btn--ok fin-pp__btn--pill" onClick={() => setShowModalAprovar(true)}>
                                <FaCheck size={14} /> Aprovar
                            </button>
                            <button type="button" className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--pill" onClick={() => setShowModalReprovar(true)}>
                                <FaTimes size={14} /> Reprovar
                            </button>
                        </div>
                    ) : null}
                </div>

                {remessaResumo.quantidade_linhas > 0 && <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                    <h2 className="fin-pp__cphead-title">Estimativa de pagamento no exterior · BRL</h2>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 28px', marginTop: 8 }}>
                        {[['pre', 'Pré-evento', 'preEvento', 'totalEstimadoPreEventoBRL'], ['neg', 'Negociado', 'negociado', 'totalEstimadoNegociadoBRL']].map(([etapa, nome, chave, campoApi]) => {
                            // Pagamentos no exterior à parte; o total do orçamento (com linhas Brasil) é a linha secundária.
                            // Total do orçamento = soma dos R$ das linhas + NF Access + encargos (contrato 02/10; item 3:
                            // mesma conta da tela e do Excel). O totalEstimado*BRL da API só entra se o orçamento não
                            // tiver linhas para somar.
                            const pagamentos = pagamentosExteriorEtapa(remessaResumo, etapa, totaisBrl.total[chave], (dados.orcamentoitem || []).length ? null : dados[campoApi]);
                            return <div key={etapa}>
                                <strong className="d-block">{nome} · Pagamentos no exterior: principal R$ {moeda(pagamentos.principal)} + encargos R$ {moeda(pagamentos.encargos)} = R$ {moeda(pagamentos.total)}</strong>
                                <small className="text-muted">Total do orçamento com encargos R$ {moeda(pagamentos.totalOrcamento)}</small>
                            </div>;
                        })}
                    </div>
                    <small className={remessaResumo.completo ? 'text-muted' : 'text-warning'}>{remessaResumo.completo ? (String(dados?.evento?.nacionalidade ?? '').toLowerCase() === 'internacional' ? 'Estimativas em reais, sem nova conversão; no evento internacional entram na base do FEE. Não são pagamentos realizados.' : 'Estimativas em reais, fora da base de FEE e sem nova conversão. Não são pagamentos realizados.') : 'Estimativa incompleta: falta a quantidade manual de envios em uma etapa com serviço positivo.'}</small>
                </div>}
                <FechamentoExteriorResumo orcamento={dados} />

                <div className="fin-pp__statusbar" style={{ marginBottom: 16 }}>
                    <div className="fin-pp__statuschip">
                        <span className="fin-pp__statuschip-dot" />
                        <span className="fin-pp__statuschip-num">{isInternacional ? `${simbolo} ${moeda(totalPreEvento)} · R$ ${moeda(subtotalBrl.preEvento)}` : moeda(subtotalBrl.preEvento)}</span>
                        <span>{remessaResumo.quantidade_linhas ? 'Subtotal pré-evento (moeda do orçamento)' : 'Pré-evento'}</span>
                    </div>
                    <div className="fin-pp__statuschip">
                        <span className="fin-pp__statuschip-dot" style={{ background: 'var(--fin-wn)' }} />
                        <span className="fin-pp__statuschip-num">{isInternacional ? `${simbolo} ${moeda(totalNegociado)} · R$ ${moeda(subtotalBrl.negociado)}` : moeda(subtotalBrl.negociado)}</span>
                        <span>{remessaResumo.quantidade_linhas ? 'Subtotal negociado (moeda do orçamento)' : 'Negociado'}</span>
                    </div>
                    <div className="fin-pp__statuschip">
                        <span className="fin-pp__statuschip-dot" style={{ background: 'var(--fin-ok)' }} />
                        <span className="fin-pp__statuschip-num">{isInternacional ? `${simbolo} ${moeda(totalFechamento)} · R$ ${moeda(subtotalBrl.fechamento)}` : moeda(subtotalBrl.fechamento)}</span>
                        <span>{dados.fechamento_real_ativo ? 'Fechamento comercial (moeda do orçamento)' : 'Fechamento'}</span>
                    </div>
                </div>

                <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                    <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                        <div>
                            <h2 className="fin-pp__cphead-title">Dados do orçamento</h2>
                            <p className="fin-pp__cphead-sub">Resumo geral e controle de adiantamento.</p>
                        </div>
                    </div>
                    <div className="fin-pp__filters">
                        <FinField label="Título" full>
                            <FinInput value={dados.titulo || ''} readOnly />
                        </FinField>
                        <FinField label="Data">
                            <FinInput value={dados.data ? moment(dados.data).format('DD/MM/YYYY') : ''} readOnly />
                        </FinField>
                        <FinField label="Valor de Adiantamento">
                            <FinInput value={moeda(virgulaPonto(dados.valorAdiantamento || 0))} readOnly style={{ textAlign: 'right' }} />
                        </FinField>
                        <FinField label="Total Antecipado">
                            <FinInput value={moeda(totalAntecipado)} readOnly style={{ textAlign: 'right' }} />
                        </FinField>
                        <FinField label="Saldo Disponível">
                            <FinInput value={moeda(saldoDisponivel)} readOnly style={{ textAlign: 'right' }} />
                        </FinField>
                        {isInternacional ? (
                            <FinField label="Câmbio">
                                <FinInput value={`1 ${simbolo} = R$ ${cambio.toFixed(4)}`} readOnly />
                            </FinField>
                        ) : null}
                        <FinField label="Observações sobre Adiantamento" full>
                            <FinTextarea rows={2} value={dados.observacaoAdiantamento || ''} readOnly />
                        </FinField>
                    </div>
                </div>

                {Array.isArray(dados.orcamentoproduto) && dados.orcamentoproduto.length > 0 ? (
                    <div className="fin-pp__card" style={{ overflow: 'hidden', marginBottom: 16 }}>
                        <div style={{ padding: 16 }}>
                            <div className="fin-pp__cphead">
                                <div>
                                    <h2 className="fin-pp__cphead-title">Produtos</h2>
                                    <p className="fin-pp__cphead-sub">Distribuição percentual do orçamento por produto.</p>
                                </div>
                            </div>
                        </div>
                        <div className="fin-pp__tblwrap">
                            <table className="fin-pp__tbl" style={{ minWidth: 520, tableLayout: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th>Produto</th>
                                        <th style={{ width: 180, textAlign: 'right' }}>Percentual (%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dados.orcamentoproduto.map((produto, index) => (
                                        <tr key={index}>
                                            <td className="fin-pp__cell-strong">{produto.produto?.nome || ''}</td>
                                            <td style={{ textAlign: 'right' }}>{pontoVirgula(produto.percentual || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : null}

                <div className="fin-pp__card" style={{ padding: 16 }}>
                    <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                        <div>
                            <h2 className="fin-pp__cphead-title">Itens</h2>
                            <p className="fin-pp__cphead-sub">Comparativo entre pré-evento, negociado e fechamento.</p>
                        </div>
                    </div>

                    {(dados.orcamentoitem || []).map((grupo, indexGrupo) => (
                        <div key={indexGrupo} className="fin-pp__card" style={{ overflow: 'hidden', marginBottom: 16, boxShadow: 'none', border: '1px solid var(--fin-bd)' }}>
                            <div style={{ padding: 16, borderBottom: '1px solid var(--fin-bd)', background: 'var(--fin-sf)' }}>
                                <div className="fin-pp__cell-strong">{AgenciaOrcamentoPolicy.nomeGrupoExibicao(grupo.produto) || 'Grupo'}</div>
                            </div>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl" style={{ minWidth: 980, tableLayout: 'auto' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ minWidth: 260 }}>Subitem</th>
                                            <th style={{ width: 180, textAlign: 'right' }}>Pré-evento</th>
                                            <th style={{ width: 180, textAlign: 'right' }}>Negociado</th>
                                            <th style={{ width: 180, textAlign: 'right' }}>Fechamento</th>
                                            <th style={{ width: 140 }}>Arquivo NF</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(grupo.subitem || []).map((subitem, indexSubitem) => {
                                            const remessa = calcularRemessaExteriorEstimativa(subitem, dados, null, { exato: true });
                                            return <React.Fragment key={`${indexGrupo}-${indexSubitem}`}><tr>
                                                <td className="fin-pp__cell-strong">{AgenciaOrcamentoPolicy.nomeSubitemExibicao(grupo, subitem.produto)}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {temCambioProprio(subitem) ? textoValorLinhaPropria(subitem, 'totalPreEvento') : isInternacional
                                                        ? (linhaFeeSoEmReal(subitem, isInternacional) ? `R$ ${moeda(brlLinhaLegada(subitem, 'totalPreEvento'))}` : `${simbolo} ${moeda(virgulaPonto(subitem.totalPreEvento || 0))} · R$ ${moeda(brlLinhaLegada(subitem, 'totalPreEvento'))}`)
                                                        : moeda(brlLinhaLegada(subitem, 'totalPreEvento'))}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {temCambioProprio(subitem) ? textoValorLinhaPropria(subitem, 'totalNegociado') : isInternacional
                                                        ? (linhaFeeSoEmReal(subitem, isInternacional) ? `R$ ${moeda(brlLinhaLegada(subitem, 'totalNegociado'))}` : `${simbolo} ${moeda(virgulaPonto(subitem.totalNegociado || 0))} · R$ ${moeda(brlLinhaLegada(subitem, 'totalNegociado'))}`)
                                                        : moeda(brlLinhaLegada(subitem, 'totalNegociado'))}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {textoFechamentoExibicao(subitem) || (temCambioProprio(subitem) ? textoValorLinhaPropria(subitem, 'totalFechamento') : isInternacional
                                                        ? (linhaFeeSoEmReal(subitem, isInternacional) ? `R$ ${moeda(brlLinhaLegada(subitem, 'totalFechamento'))}` : `${simbolo} ${moeda(virgulaPonto(subitem.totalFechamento || 0))} · R$ ${moeda(brlLinhaLegada(subitem, 'totalFechamento'))}`)
                                                        : moeda(brlLinhaLegada(subitem, 'totalFechamento')))}
                                                </td>
                                                <td>
                                                    {subitem.arquivonotafiscal?.id ? (
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => abrirNotaFiscal(subitem.arquivonotafiscal.id)}>
                                                            <FaDownload size={12} /> Baixar
                                                        </button>
                                                    ) : (
                                                        <span className="fin-pp__cell-muted">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                            {remessa.elegivel && [
                                                ['imposto_remessa_brl', 'IR e IOF'], ['taxa_envio_brl', 'Taxa de envio'],
                                                // Ajuste manual do Imposto NF Access (Inter 19/10): sem o "19,05%" fixo e com a alíquota na célula.
                                                ['imposto_nf_brl', remessa.pre.nf_ajustado || remessa.neg.nf_ajustado ? 'Imposto NF Access' : 'Imposto NF Access (19,05%)'], ['total_brl', 'Total com impostos'],
                                            ].map(([campo, nome]) => <tr key={campo} style={{ color: campo === 'total_brl' ? '#334155' : '#64748b', fontWeight: campo === 'total_brl' ? 600 : 400, fontSize: 12, background: '#f8fafc' }}>
                                                <td>↳ {nome} · estimativa Access em BRL</td>
                                                {['pre', 'neg'].map((etapa) => <td key={etapa} style={{ textAlign: 'right' }}>R$ {moeda(remessa[etapa][campo])}
                                                    {campo === 'imposto_nf_brl' && remessa[etapa].nf_ajustado && <small className="d-block" style={{ fontSize: 10, color: '#a85b07' }}>
                                                        {String(remessa[etapa].nf_percentual).replace('.', ',')}% {remessa[etapa].nf_modo === 'direto' ? 'conta direta' : 'conta invertida'} · ajuste manual</small>}</td>)}
                                                <td />
                                                <td />
                                            </tr>)}
                                            </React.Fragment>;
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showModalAprovar && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg" onClick={(e) => { if (e.target === e.currentTarget) { setShowModalAprovar(false); setComentarios(''); } }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic is-ok"><FaCheck size={17} /></span>
                                <h3 className="fin-pp__modal-title">Aprovar Orçamento</h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => { setShowModalAprovar(false); setComentarios(''); }}><FaTimes size={16} /></button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__field">
                                    <label htmlFor="comentariosAprovar" className="fin-pp__label">Comentários *</label>
                                    <textarea
                                        id="comentariosAprovar"
                                        className="fin-pp__input"
                                        style={{ height: 'auto', padding: '10px 14px' }}
                                        rows="4"
                                        value={comentarios}
                                        onChange={(e) => setComentarios(e.target.value)}
                                        placeholder="Descreva os comentários sobre a aprovação..."
                                        required
                                    ></textarea>
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => { setShowModalAprovar(false); setComentarios(''); }}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--ok" onClick={aprovar} disabled={!comentarios.trim()}>
                                    <FaCheck size={16} /> Aprovar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showModalReprovar && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg" onClick={(e) => { if (e.target === e.currentTarget) { setShowModalReprovar(false); setComentarios(''); } }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic is-danger"><FaTimes size={17} /></span>
                                <h3 className="fin-pp__modal-title">Reprovar Orçamento</h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => { setShowModalReprovar(false); setComentarios(''); }}><FaTimes size={16} /></button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__field">
                                    <label htmlFor="comentariosReprovar" className="fin-pp__label">Comentários *</label>
                                    <textarea
                                        id="comentariosReprovar"
                                        className="fin-pp__input"
                                        style={{ height: 'auto', padding: '10px 14px' }}
                                        rows="4"
                                        value={comentarios}
                                        onChange={(e) => setComentarios(e.target.value)}
                                        placeholder="Descreva os comentários sobre a reprovação..."
                                        required
                                    ></textarea>
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => { setShowModalReprovar(false); setComentarios(''); }}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--danger" onClick={reprovar} disabled={!comentarios.trim()}>
                                    <FaTimes size={16} /> Reprovar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ErpTemplate>
    );
}
